'use client'

import { CheckCircle2, Loader2 } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import Image from 'next/image'
import { useRef, useState } from 'react'

interface FirstBentoAnimationProps {
  isHovered?: boolean
}

export function FirstBentoAnimation({
  isHovered = false,
}: FirstBentoAnimationProps) {
  const ref = useRef(null)
  const [isComplete, setIsComplete] = useState(false)

  return (
    <div
      ref={ref}
      className="flex h-full w-full items-center justify-center px-6 pt-6"
    >
      <motion.div
        className="flex h-full w-full max-w-5xl items-start gap-20"
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 20,
        }}
      >
        {/* Left Side - Chat */}
        <div className="flex flex-1 flex-col gap-6 pt-4">
          {/* User Message - Right Aligned */}
          <div className="flex items-end justify-end gap-2">
            <motion.div
              className="max-w-[280px] rounded-xl rounded-br-sm bg-white px-3 py-2 text-zinc-900 shadow-sm dark:bg-white"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.8,
                ease: 'easeOut',
                delay: 0.5,
              }}
            >
              <p className="text-sm leading-relaxed">
                Create an email announcing the Claude 3 models
              </p>
            </motion.div>
            <motion.div
              className="flex w-fit shrink-0 items-center rounded-full border border-zinc-200 bg-white dark:border-zinc-700"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.5,
                ease: 'easeOut',
                delay: 0.5,
              }}
            >
              <Image
                src="https://randomuser.me/api/portraits/women/79.jpg"
                alt="User Avatar"
                className="size-8 shrink-0 rounded-full"
                width={32}
                height={32}
                sizes="32px"
                quality={60}
              />
            </motion.div>
          </div>

          {/* Brew Response - Left Aligned */}
          <div className="flex items-end gap-2">
            <AnimatePresence>
              {isHovered ? (
                <motion.div
                  className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{
                    duration: 0.5,
                    ease: 'easeOut',
                    delay: 2,
                  }}
                >
                  <Image
                    src="/images/circle_paper.png"
                    alt="Brew Logo"
                    width={32}
                    height={32}
                    className="size-8"
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>

            <div className="max-w-[220px] flex-1">
              <AnimatePresence
                mode="wait"
                onExitComplete={() => setIsComplete(false)}
              >
                {isHovered ? (
                  <motion.div
                    key="tool-call"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{
                      duration: 0.8,
                      ease: 'easeOut',
                      delay: 2,
                    }}
                  >
                    <motion.div
                      className={`${
                        isComplete
                          ? 'border-green-200 bg-green-50'
                          : 'border-white/60 bg-white/80 backdrop-blur-sm'
                      } flex min-h-[48px] items-center gap-2 rounded-xl rounded-bl-sm border px-3 py-2 shadow-sm transition-colors duration-500`}
                      onAnimationComplete={() => setIsComplete(true)}
                      animate={isComplete ? {} : { opacity: 1 }}
                      transition={{
                        delay: 2.5,
                      }}
                    >
                      <motion.div
                        className="relative flex h-4 w-4 shrink-0 items-center justify-center"
                        animate={isComplete ? { scale: [1, 1.2, 1] } : {}}
                        transition={{ duration: 0.5 }}
                      >
                        {isComplete ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        )}
                      </motion.div>
                      <span
                        className={`${
                          isComplete
                            ? 'font-medium text-green-700'
                            : 'text-zinc-700'
                        } flex-1 text-sm transition-colors duration-500`}
                      >
                        {isComplete ? 'Email generated' : 'Generating...'}
                      </span>
                    </motion.div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Right Side - Email Preview */}
        <div className="flex h-full flex-1 items-center justify-center">
          <AnimatePresence mode="wait">
            {isHovered ? (
              <motion.div
                key="email-preview"
                className="mb-[-100px] min-w-[340px] rounded-t-xl bg-[#F0EFEA] shadow-xl md:min-w-[420px]"
                initial={{ opacity: 0, scale: 0.98, y: 40 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                  transition: {
                    duration: 1.5,
                    ease: [0.34, 1.56, 0.64, 1],
                    delay: 4.5,
                  },
                }}
                exit={{
                  opacity: 0,
                  scale: 0.98,
                  y: 40,
                  transition: {
                    duration: 0.8,
                    ease: [0.32, 0, 0.67, 0],
                  },
                }}
              >
                {/* Main Content Card */}
                <div className="mx-2 mt-2 overflow-hidden rounded-lg bg-white">
                  {/* Header with Logo */}
                  <div className="flex items-center justify-between px-5 pt-5 pb-4">
                    <Image
                      src="https://cdn.brew.new/marketing/anthropic-wordmark-dark.png"
                      alt="Anthropic"
                      className="h-3"
                      width={160}
                      height={18}
                      sizes="160px"
                      quality={70}
                    />
                    <div className="rounded bg-[#F0EFEA] px-3 py-1.5 font-bold text-[#141413] text-[10px]">
                      Visit Website
                    </div>
                  </div>

                  {/* Hero Content */}
                  <div className="px-5 pb-4">
                    <h1 className="mb-3 font-bold text-2xl text-[#141413] leading-tight">
                      Introducing Claude 3
                    </h1>
                    <p className="mb-4 text-[#828179] text-xs leading-relaxed">
                      Our most capable AI model family yet. Experience
                      unprecedented intelligence, safety, and reliability across
                      three powerful tiers: Opus, Sonnet, and Haiku.
                    </p>
                    <button
                      type="button"
                      className="rounded bg-[#CC785C] px-5 py-2.5 font-bold text-white text-xs"
                    >
                      Explore Claude 3
                    </button>
                  </div>

                  {/* Hero Image */}
                  <div className="overflow-hidden rounded-b-lg">
                    <Image
                      src="https://cdn.brew.new/marketing/anthropic-claude3-hero.jpeg"
                      alt="Claude 3 AI interface"
                      className="h-20 w-full object-cover"
                      width={600}
                      height={160}
                      sizes="(max-width: 768px) 100vw, 420px"
                      quality={75}
                    />
                  </div>
                </div>

                {/* Three Models Section */}
                <div className="mx-2 mt-2 overflow-hidden rounded-lg bg-white">
                  <div className="px-5 pt-4 pb-2">
                    <h2 className="mb-3 font-bold text-[#141413] text-base">
                      Three models, one mission
                    </h2>

                    {/* Model Cards */}
                    <div className="mb-4 space-y-3">
                      {/* Opus */}
                      <div className="flex gap-2">
                        <Image
                          src="https://placehold.co/64x64/CC785C/ffffff?text=Opus"
                          alt="Claude 3 Opus"
                          className="h-16 w-16 shrink-0 rounded object-cover"
                          width={64}
                          height={64}
                          sizes="64px"
                          quality={65}
                        />
                        <div>
                          <h3 className="mb-0.5 font-bold text-[#141413] text-xs">
                            Claude 3 Opus
                          </h3>
                          <p className="text-[#828179] text-[10px] leading-snug">
                            Our most intelligent model for complex analysis,
                            advanced reasoning, and nuanced content creation.
                          </p>
                        </div>
                      </div>

                      {/* Sonnet */}
                      <div className="flex gap-2">
                        <Image
                          src="https://placehold.co/64x64/828179/ffffff?text=Sonnet"
                          alt="Claude 3 Sonnet"
                          className="h-16 w-16 shrink-0 rounded object-cover"
                          width={64}
                          height={64}
                          sizes="64px"
                          quality={65}
                        />
                        <div>
                          <h3 className="mb-0.5 font-bold text-[#141413] text-xs">
                            Claude 3 Sonnet
                          </h3>
                          <p className="text-[#828179] text-[10px] leading-snug">
                            The ideal balance of intelligence and speed for
                            scaled AI deployments across your organization.
                          </p>
                        </div>
                      </div>

                      {/* Haiku */}
                      <div className="flex gap-2">
                        <Image
                          src="https://placehold.co/64x64/F0EFEA/141413?text=Haiku"
                          alt="Claude 3 Haiku"
                          className="h-16 w-16 shrink-0 rounded object-cover"
                          width={64}
                          height={64}
                          sizes="64px"
                          quality={65}
                        />
                        <div>
                          <h3 className="mb-0.5 font-bold text-[#141413] text-xs">
                            Claude 3 Haiku
                          </h3>
                          <p className="text-[#828179] text-[10px] leading-snug">
                            Our most intelligent model for complex analysis,
                            advanced reasoning, and nuanced content creation.
                          </p>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="w-full rounded bg-[#F0EFEA] py-2 font-bold text-[#141413] text-xs"
                    >
                      Compare Models
                    </button>
                  </div>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  )
}
